import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AcademicContextSelector from './AcademicContextSelector';
import COEHeader from './COEHeader';
import { 
  Plus, Edit, Trash2, CheckCircle, Save, X, 
  PlusCircle, Trash, Layers, AlertCircle, RefreshCw, ChevronRight, 
  Copy, Calendar, Lock, Unlock, Award, TrendingUp, EyeOff,
  Power, Eye as EyeIcon, PenTool, Clock, PieChart, Filter,
  ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../api';

const ExamComponentManager = () => {
  const navigate = useNavigate();
  
  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [contextSelected, setContextSelected] = useState(false);
  const [context, setContext] = useState({});
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAlert, setShowAlert] = useState({ show: false, message: '', type: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [orgInfo, setOrgInfo] = useState({ name: '', logoUrl: '' });
  const [tenant, setTenant] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [mainExpanded, setMainExpanded] = useState({});
  const [showMatchingRules, setShowMatchingRules] = useState(false);
  const [expandedEndExamItems, setExpandedEndExamItems] = useState({});
  
  // Template type selection
  const [selectedTemplateType, setSelectedTemplateType] = useState('both');
  
  // Matching Rules State
  const [matchingRules, setMatchingRules] = useState({
    applicableSubjectTypes: [],
    subjectCodePatterns: [],
    subjectNamePatterns: [],
    isLab: null,
    isElective: null,
    isLanguage: null,
    isProject: null,
    priority: 100,
    isDefault: false
  });
  
  // Delete confirmation modal states
  const [showDeleteDefinitionModal, setShowDeleteDefinitionModal] = useState(false);
  const [deleteDefinitionId, setDeleteDefinitionId] = useState(null);
  const [deleteDefinitionName, setDeleteDefinitionName] = useState('');
  const [deleteDefinitionLoading, setDeleteDefinitionLoading] = useState(false);
  
  const [showDeleteComponentModal, setShowDeleteComponentModal] = useState(false);
  const [deleteComponentIdx, setDeleteComponentIdx] = useState(null);
  const [deleteComponentName, setDeleteComponentName] = useState('');
  const [deleteComponentLoading, setDeleteComponentLoading] = useState(false);
  
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState({ compIdx: null, itemIdx: null, itemName: '', compName: '' });
  const [deleteItemLoading, setDeleteItemLoading] = useState(false);

  // NEW: End Exam item delete modal
  const [showDeleteEndExamItemModal, setShowDeleteEndExamItemModal] = useState(false);
  const [deleteEndExamItemData, setDeleteEndExamItemData] = useState({ itemIdx: null, itemName: '' });
  const [deleteEndExamItemLoading, setDeleteEndExamItemLoading] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'both',
    finalGradeCombination: {
      ciePercentage: 40,
      endExamPercentage: 60
    },
    cieEnabled: true,
    cieComponents: [],
    endExamEnabled: true,
    endExam: {
      name: 'End Examination',
      status: 'active',
      isVisibleToFaculty: true,
      allowFacultyEdit: true,
      isVisibleToStudents: false,
      requirePaperTemplate: false,
      hasEndDate: false,
      endDate: '',
      // NEW: Multiple items support for End Exam
      hasMultipleItems: false,
      items: [],
      aggregationType: 'sum_of_all',
      nValue: 1,
      totalMaxMarks: null
    }
  });

  const selectedProgramType = programTypes.find(pt => pt._id === context.programTypeId);
  const selectedProgram = programs.find(p => p._id === context.programId);
  const selectedAcademicYear = academicYears.find(ay => ay._id === context.academicYearId);

  useEffect(() => {
    const name = localStorage.getItem('tenantName') || '';
    const subdomain = localStorage.getItem('tenantSubdomain') || '';
    setTenant({ name, subdomain });
    
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      api.get(`/api/organization/basic-info/${tenantId}`)
        .then(res => setOrgInfo({ name: res.data?.name || '', logoUrl: res.data?.logoUrl || '' }))
        .catch(() => setOrgInfo({ name: '', logoUrl: '' }));
    }
  }, []);

  const fetchDefinitionsCallback = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/cie/cie-component', {
        params: {
          academicYearId: context.academicYearId,
          programId: context.programId,
          ...(context.evaluationMode ? { evaluationMode: context.evaluationMode } : {}),
          ...(context.semId ? { semId: context.semId } : {}),
          ...(context.termId ? { termId: context.termId } : {})
        }
      });
      setDefinitions(response.data.data || []);
      const expandedState = {};
      response.data.data.forEach(def => {
        expandedState[def._id] = {};
        (def.cie.components || []).forEach((comp, idx) => {
          expandedState[def._id][idx] = false;
        });
      });
      setMainExpanded(expandedState);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [context.academicYearId, context.programId, context.evaluationMode, context.semId, context.termId]);

  useEffect(() => {
    if (context.academicYearId && context.programId && context.programTypeId) {
      fetchDefinitionsCallback();
    }
  }, [context, fetchDefinitionsCallback]);

  const handleContextChange = React.useCallback((newContext) => {
    setContext(newContext);
    setContextSelected(!!(newContext.academicYearId && newContext.programId && newContext.programTypeId));
  }, []);

  const showAlertMessage = (message, type = 'error') => {
    setShowAlert({ show: true, message, type });
    setTimeout(() => setShowAlert({ show: false, message: '', type: '' }), 3000);
  };

  const toggleComponentExpand = (id) => {
    setExpandedComponents(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleItemExpand = (compId, itemIdx) => {
    const key = `${compId}_${itemIdx}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleEndExamItemExpand = (itemIdx) => {
    setExpandedEndExamItems(prev => ({ ...prev, [itemIdx]: !prev[itemIdx] }));
  };

  // ==================== MATCHING RULES FUNCTIONS ====================
  const toggleSubjectType = (type) => {
    setMatchingRules(prev => {
      const current = prev.applicableSubjectTypes;
      if (current.includes(type)) {
        return { ...prev, applicableSubjectTypes: current.filter(t => t !== type) };
      } else {
        return { ...prev, applicableSubjectTypes: [...current, type] };
      }
    });
  };

  const resetMatchingRules = () => {
    setMatchingRules({
      applicableSubjectTypes: [],
      subjectCodePatterns: [],
      subjectNamePatterns: [],
      isLab: null,
      isElective: null,
      isLanguage: null,
      isProject: null,
      priority: 100,
      isDefault: false
    });
  };

  // ==================== END EXAM ITEM FUNCTIONS (NEW) ====================
  const addEndExamItem = () => {
    setFormData(prev => ({
      ...prev,
      endExam: {
        ...prev.endExam,
        items: [...prev.endExam.items, {
          itemId: Date.now(),
          name: '',
          referenceMaxMarks: 0,
          endDate: '',
          isFrozen: false,
          requirePaperTemplate: false
        }]
      }
    }));
  };

  const updateEndExamItem = (itemIdx, field, value) => {
    const updatedItems = [...formData.endExam.items];
    updatedItems[itemIdx][field] = value;
    setFormData(prev => ({
      ...prev,
      endExam: {
        ...prev.endExam,
        items: updatedItems,
        totalMaxMarks: updatedItems.reduce((sum, item) => sum + (item.referenceMaxMarks || 0), 0)
      }
    }));
  };

  const confirmDeleteEndExamItem = (itemIdx, itemName) => {
    setDeleteEndExamItemData({ itemIdx, itemName });
    setShowDeleteEndExamItemModal(true);
  };

  const handleDeleteEndExamItem = () => {
    const { itemIdx } = deleteEndExamItemData;
    if (itemIdx === null) return;
    setDeleteEndExamItemLoading(true);
    setTimeout(() => {
      const updatedItems = [...formData.endExam.items];
      updatedItems.splice(itemIdx, 1);
      setFormData(prev => ({
        ...prev,
        endExam: {
          ...prev.endExam,
          items: updatedItems,
          totalMaxMarks: updatedItems.reduce((sum, item) => sum + (item.referenceMaxMarks || 0), 0)
        }
      }));
      setShowDeleteEndExamItemModal(false);
      setDeleteEndExamItemData({ itemIdx: null, itemName: '' });
      setDeleteEndExamItemLoading(false);
      showAlertMessage('End Exam item deleted successfully', 'success');
    }, 500);
  };

  // ==================== COMPONENT FUNCTIONS ====================
  
  const addComponent = () => {
    const newId = Date.now();
    setFormData({
      ...formData,
      cieComponents: [...formData.cieComponents, {
        id: newId,
        name: '',
        percentageOfCie: 0,
        status: 'active',
        isFrozen: false,
        isVisibleToFaculty: true,
        allowFacultyEdit: true,
        isVisibleToStudents: true,
        type: 'multiple',
        requirePaperTemplate: false,
        items: [{ 
          itemId: Date.now(),
          name: '',
          referenceMaxMarks: 0,
          endDate: '',
          isFrozen: false,
          requirePaperTemplate: false
        }],
        aggregationType: 'average_of_all',
        nValue: 1,
        attendanceRules: [{ minPercentage: 75, maxPercentage: 100, marksPercentage: 0 }],
        referenceMaxMarks: null,
        hasEndDate: false,
        singleItemEndDate: ''
      }]
    });
    setExpandedComponents(prev => ({ ...prev, [newId]: true }));
  };

  const updateComponent = (index, field, value) => {
    const updated = [...formData.cieComponents];
    updated[index][field] = value;
    setFormData({ ...formData, cieComponents: updated });
  };

  const confirmDeleteComponent = (compIndex, compName) => {
    setDeleteComponentIdx(compIndex);
    setDeleteComponentName(compName);
    setShowDeleteComponentModal(true);
  };

  const handleDeleteComponent = () => {
    if (deleteComponentIdx === null) return;
    setDeleteComponentLoading(true);
    setTimeout(() => {
      const updated = [...formData.cieComponents];
      const removedId = updated[deleteComponentIdx].id;
      updated.splice(deleteComponentIdx, 1);
      setFormData({ ...formData, cieComponents: updated });
      const newExpanded = { ...expandedComponents };
      delete newExpanded[removedId];
      setExpandedComponents(newExpanded);
      setShowDeleteComponentModal(false);
      setDeleteComponentIdx(null);
      setDeleteComponentName('');
      setDeleteComponentLoading(false);
      showAlertMessage('Component deleted successfully', 'success');
    }, 500);
  };

  const duplicateComponent = (index) => {
    const component = JSON.parse(JSON.stringify(formData.cieComponents[index]));
    component.id = Date.now();
    component.name = `${component.name} (Copy)`;
    const updated = [...formData.cieComponents];
    updated.splice(index + 1, 0, component);
    setFormData({ ...formData, cieComponents: updated });
    setExpandedComponents(prev => ({ ...prev, [component.id]: true }));
  };

  const addItem = (compIndex) => {
    const updated = [...formData.cieComponents];
    updated[compIndex].items.push({ 
      itemId: Date.now(),
      name: '',
      referenceMaxMarks: 0,
      endDate: '',
      isFrozen: false,
      requirePaperTemplate: false
    });
    setFormData({ ...formData, cieComponents: updated });
  };

  const updateItem = (compIndex, itemIndex, field, value) => {
    const updated = [...formData.cieComponents];
    updated[compIndex].items[itemIndex][field] = value;
    setFormData({ ...formData, cieComponents: updated });
  };

  const confirmDeleteItem = (compIdx, itemIdx, itemName, compName) => {
    setDeleteItemData({ compIdx, itemIdx, itemName, compName });
    setShowDeleteItemModal(true);
  };

  const handleDeleteItem = () => {
    const { compIdx, itemIdx } = deleteItemData;
    if (compIdx === null || itemIdx === null) return;
    setDeleteItemLoading(true);
    setTimeout(() => {
      const updated = [...formData.cieComponents];
      updated[compIdx].items.splice(itemIdx, 1);
      setFormData({ ...formData, cieComponents: updated });
      setShowDeleteItemModal(false);
      setDeleteItemData({ compIdx: null, itemIdx: null, itemName: '', compName: '' });
      setDeleteItemLoading(false);
      showAlertMessage('Item deleted successfully', 'success');
    }, 500);
  };

  const addAttendanceRule = (compIndex) => {
    const updated = [...formData.cieComponents];
    updated[compIndex].attendanceRules.push({ minPercentage: 0, maxPercentage: 100, marksPercentage: 0 });
    setFormData({ ...formData, cieComponents: updated });
  };

  const updateAttendanceRule = (compIndex, ruleIndex, field, value) => {
    const updated = [...formData.cieComponents];
    updated[compIndex].attendanceRules[ruleIndex][field] = value;
    setFormData({ ...formData, cieComponents: updated });
  };

  const removeAttendanceRule = (compIndex, ruleIndex) => {
    const updated = [...formData.cieComponents];
    updated[compIndex].attendanceRules.splice(ruleIndex, 1);
    setFormData({ ...formData, cieComponents: updated });
  };

  const getTotalComponentPercentage = () => {
    return formData.cieComponents.reduce((sum, c) => sum + (c.percentageOfCie || 0), 0);
  };

  const isTotalValid = () => {
    return Math.abs(getTotalComponentPercentage() - 100) < 0.01;
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.name.trim()) {
        showAlertMessage('Please enter a template name', 'error');
        return false;
      }
      if (!contextSelected) {
        showAlertMessage('Please select Program Type, Program, and Academic Year', 'error');
        return false;
      }
      if (selectedTemplateType === 'both') {
        if (formData.finalGradeCombination.ciePercentage + formData.finalGradeCombination.endExamPercentage !== 100) {
          showAlertMessage('CIE% + End Exam% must equal 100%', 'error');
          return false;
        }
      }
      return true;
    }
    
    if (currentStep === 2) {
      if (selectedTemplateType === 'end_exam_only') {
        return true;
      }
      
      if (formData.cieComponents.length === 0) {
        showAlertMessage('Please add at least one component', 'error');
        return false;
      }
      
      for (let i = 0; i < formData.cieComponents.length; i++) {
        const comp = formData.cieComponents[i];
        if (!comp.name.trim()) {
          showAlertMessage(`Component ${i + 1}: Please enter a name`, 'error');
          return false;
        }
        if (!comp.percentageOfCie || comp.percentageOfCie <= 0) {
          showAlertMessage(`Component ${i + 1}: Please enter valid percentage`, 'error');
          return false;
        }
        
        if (comp.type === 'attendance') {
          if (comp.attendanceRules.length === 0) {
            showAlertMessage(`Attendance component "${comp.name}": Please add at least one rule`, 'error');
            return false;
          }
        } else if (comp.type === 'multiple') {
          for (let j = 0; j < comp.items.length; j++) {
            const item = comp.items[j];
            if (!item.name.trim()) {
              showAlertMessage(`${comp.name}: Item ${j + 1} name is required`, 'error');
              return false;
            }
            if (!item.referenceMaxMarks || item.referenceMaxMarks <= 0) {
              showAlertMessage(`${comp.name}: Item ${j + 1} reference max marks is required`, 'error');
              return false;
            }
          }
        } else if (comp.type === 'single') {
          if (!comp.referenceMaxMarks || comp.referenceMaxMarks <= 0) {
            showAlertMessage(`${comp.name}: Reference max marks is required`, 'error');
            return false;
          }
        }
      }
      
      if (!isTotalValid()) {
        showAlertMessage(`Total component percentage (${getTotalComponentPercentage()}%) must equal 100%`, 'error');
        return false;
      }
      return true;
    }
    
    if (currentStep === 3 && selectedTemplateType !== 'cie_only') {
      if (!formData.endExam.name.trim()) {
        showAlertMessage('Please enter End Exam name', 'error');
        return false;
      }
      
      // Validate End Exam items if hasMultipleItems is true
      if (formData.endExam.hasMultipleItems && formData.endExam.items.length === 0) {
        showAlertMessage('Please add at least one item to End Exam', 'error');
        return false;
      }
      
      for (let i = 0; i < formData.endExam.items.length; i++) {
        const item = formData.endExam.items[i];
        if (!item.name.trim()) {
          showAlertMessage(`End Exam item ${i + 1}: Name is required`, 'error');
          return false;
        }
        if (!item.referenceMaxMarks || item.referenceMaxMarks <= 0) {
          showAlertMessage(`End Exam item ${i + 1}: Reference max marks is required`, 'error');
          return false;
        }
      }
      
      return true;
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleTemplateTypeChange = (type) => {
    setSelectedTemplateType(type);
    setFormData({
      ...formData,
      templateType: type,
      finalGradeCombination: type === 'both' ? { ciePercentage: 40, endExamPercentage: 60 } : null,
      cieEnabled: type !== 'end_exam_only',
      endExamEnabled: type !== 'cie_only'
    });
  };

  const [saveLoading, setSaveLoading] = useState(false);

  const handleSave = async () => {
    if (!validateStep()) return;
    setSaveLoading(true);

    const cieComponents = (selectedTemplateType !== 'end_exam_only') ? formData.cieComponents.map(c => {
      if (c.type === 'attendance') {
        return {
          name: c.name,
          percentageOfCie: c.percentageOfCie,
          status: c.status,
          isVisibleToFaculty: c.isVisibleToFaculty,
          allowFacultyEdit: c.allowFacultyEdit,
          isVisibleToStudents: c.isVisibleToStudents,
          type: 'attendance',
          isAttendance: true,
          requirePaperTemplate: typeof c.requirePaperTemplate === 'boolean' ? c.requirePaperTemplate : false,
          attendanceRules: c.attendanceRules.map(rule => ({
            minPercentage: rule.minPercentage,
            maxPercentage: rule.maxPercentage,
            marksPercentage: rule.marksPercentage
          }))
        };
      }
      
      if (c.type === 'single') {
        return {
          name: c.name,
          percentageOfCie: c.percentageOfCie,
          status: c.status,
          isVisibleToFaculty: c.isVisibleToFaculty,
          allowFacultyEdit: c.allowFacultyEdit,
          isVisibleToStudents: c.isVisibleToStudents,
          type: 'single',
          referenceMaxMarks: c.referenceMaxMarks,
          hasEndDate: c.hasEndDate,
          singleItemEndDate: c.singleItemEndDate,
          requirePaperTemplate: typeof c.requirePaperTemplate === 'boolean' ? c.requirePaperTemplate : false
        };
      }
      
      return {
        name: c.name,
        percentageOfCie: c.percentageOfCie,
        status: c.status,
        isVisibleToFaculty: c.isVisibleToFaculty,
        allowFacultyEdit: c.allowFacultyEdit,
        isVisibleToStudents: c.isVisibleToStudents,
        type: 'multiple',
        hasMultipleItems: true,
        requirePaperTemplate: typeof c.requirePaperTemplate === 'boolean' ? c.requirePaperTemplate : false,
        items: c.items.map(i => ({ 
          name: i.name, 
          referenceMaxMarks: i.referenceMaxMarks, 
          endDate: i.endDate,
          isFrozen: false,
          requirePaperTemplate: typeof i.requirePaperTemplate === 'boolean' ? i.requirePaperTemplate : false
        })),
        aggregationRule: {
          type: c.aggregationType,
          nValue: (c.aggregationType === 'best_of_n' || c.aggregationType === 'last_n' || c.aggregationType === 'first_n') ? c.nValue : null
        }
      };
    }) : [];

    if (!context.evaluationMode) {
      showAlertMessage('Please select Semester or Term before creating a template.', 'error');
      return;
    }

    // Build End Exam payload with multiple items support
    let endExamPayload = {
      enabled: selectedTemplateType !== 'cie_only',
      name: formData.endExam.name,
      status: formData.endExam.status,
      isVisibleToFaculty: formData.endExam.isVisibleToFaculty,
      allowFacultyEdit: formData.endExam.allowFacultyEdit,
      isVisibleToStudents: formData.endExam.isVisibleToStudents,
      requirePaperTemplate: !!formData.endExam.requirePaperTemplate,
      hasEndDate: formData.endExam.hasEndDate,
      endDate: formData.endExam.endDate,
      hasMultipleItems: formData.endExam.hasMultipleItems || false,
      aggregationRule: {
        type: formData.endExam.aggregationType || 'sum_of_all',
        nValue: (formData.endExam.aggregationType === 'best_of_n' || formData.endExam.aggregationType === 'last_n' || formData.endExam.aggregationType === 'first_n') ? formData.endExam.nValue : null
      }
    };

    // Add items if hasMultipleItems is true
    if (formData.endExam.hasMultipleItems && formData.endExam.items.length > 0) {
      endExamPayload.items = formData.endExam.items.map(item => ({
        name: item.name,
        referenceMaxMarks: item.referenceMaxMarks,
        endDate: item.endDate || null,
        isFrozen: false,
        requirePaperTemplate: typeof item.requirePaperTemplate === 'boolean' ? item.requirePaperTemplate : false
      }));
      endExamPayload.totalMaxMarks = formData.endExam.items.reduce((sum, item) => sum + (item.referenceMaxMarks || 0), 0);
    } else {
      endExamPayload.items = [];
      // For backward compatibility, keep referenceMaxMarks if exists
      if (formData.endExam.totalMaxMarks) {
        endExamPayload.referenceMaxMarks = formData.endExam.totalMaxMarks;
      }
    }

    const payload = {
      tenantId: localStorage.getItem('tenantId'),
      name: formData.name,
      description: formData.description,
      createdBy: localStorage.getItem('userId'),
      templateType: selectedTemplateType,
      context: {
        academicYearId: context.academicYearId || '',
        academicYearName: context.academicYearName || selectedAcademicYear?.name || '',
        programTypeId: context.programTypeId || '',
        programTypeCode: selectedProgramType?.code || '',
        programTypeName: selectedProgramType?.name || '',
        programId: context.programId || '',
        programCode: selectedProgram?.code || '',
        programName: selectedProgram?.name || ''
      },
      evaluationMode: context.evaluationMode,
      semId: context.semId || null,
      termId: context.termId || null,
      finalGradeCombination: selectedTemplateType === 'both' ? {
        ciePercentage: formData.finalGradeCombination.ciePercentage,
        endExamPercentage: formData.finalGradeCombination.endExamPercentage
      } : null,
      cie: {
        enabled: selectedTemplateType !== 'end_exam_only',
        components: cieComponents
      },
      endExam: endExamPayload,
      matchingRules: {
        applicableSubjectTypes: matchingRules.applicableSubjectTypes,
        subjectCodePatterns: matchingRules.subjectCodePatterns,
        subjectNamePatterns: matchingRules.subjectNamePatterns,
        isLab: matchingRules.isLab,
        isElective: matchingRules.isElective,
        isLanguage: matchingRules.isLanguage,
        isProject: matchingRules.isProject,
        priority: matchingRules.priority,
        isDefault: matchingRules.isDefault
      }
    };

    try {
      if (editingItem) {
        await api.put(`/api/cie/cie-component/${editingItem._id}`, { ...payload, updatedBy: localStorage.getItem('userId') });
        showAlertMessage('Template updated successfully', 'success');
      } else {
        await api.post('/api/cie/cie-component', payload);
        showAlertMessage('Template created successfully', 'success');
      }
      fetchDefinitionsCallback();
      setShowModal(false);
      resetForm();
      setCurrentStep(1);
    } catch (error) {
      showAlertMessage(error.response?.data?.message || 'Error saving template', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setSelectedTemplateType('both');
    setFormData({
      name: '',
      description: '',
      templateType: 'both',
      finalGradeCombination: {
        ciePercentage: 40,
        endExamPercentage: 60
      },
      cieEnabled: true,
      cieComponents: [],
      endExamEnabled: true,
      endExam: {
        name: 'End Examination',
        status: 'active',
        isVisibleToFaculty: true,
        allowFacultyEdit: true,
        isVisibleToStudents: false,
        requirePaperTemplate: false,
        hasEndDate: false,
        endDate: '',
        hasMultipleItems: false,
        items: [],
        aggregationType: 'sum_of_all',
        nValue: 1,
        totalMaxMarks: null
      }
    });
    setExpandedComponents({});
    setExpandedItems({});
    setExpandedEndExamItems({});
    resetMatchingRules();
    setShowMatchingRules(false);
  };

  const editDefinition = (item) => {
    setEditingItem(item);
    setSelectedTemplateType(item.templateType || 'both');
    
    if (item.matchingRules) {
      setMatchingRules({
        applicableSubjectTypes: item.matchingRules.applicableSubjectTypes || [],
        subjectCodePatterns: item.matchingRules.subjectCodePatterns || [],
        subjectNamePatterns: item.matchingRules.subjectNamePatterns || [],
        isLab: item.matchingRules.isLab !== undefined ? item.matchingRules.isLab : null,
        isElective: item.matchingRules.isElective !== undefined ? item.matchingRules.isElective : null,
        isLanguage: item.matchingRules.isLanguage !== undefined ? item.matchingRules.isLanguage : null,
        isProject: item.matchingRules.isProject !== undefined ? item.matchingRules.isProject : null,
        priority: item.matchingRules.priority || 100,
        isDefault: item.matchingRules.isDefault || false
      });
    } else {
      resetMatchingRules();
    }
    
    const mappedComponents = (item.cie?.components || []).map((c, idx) => {
      if (c.isAttendance === true || c.type === 'attendance') {
        return {
          id: Date.now() + idx,
          name: c.name,
          percentageOfCie: c.percentageOfCie,
          status: c.status || 'active',
          isFrozen: c.isFrozen || false,
          isVisibleToFaculty: c.isVisibleToFaculty !== undefined ? c.isVisibleToFaculty : true,
          allowFacultyEdit: c.allowFacultyEdit !== undefined ? c.allowFacultyEdit : true,
          isVisibleToStudents: c.isVisibleToStudents !== undefined ? c.isVisibleToStudents : true,
          type: 'attendance',
          items: [],
          aggregationType: 'average_of_all',
          nValue: 1,
          attendanceRules: (c.attendanceRules && c.attendanceRules.length > 0) ? c.attendanceRules.map(r => ({
            minPercentage: r.minPercentage,
            maxPercentage: r.maxPercentage,
            marksPercentage: r.marksPercentage
          })) : [{ minPercentage: 75, maxPercentage: 100, marksPercentage: 0 }],
          referenceMaxMarks: null,
          hasEndDate: false,
          singleItemEndDate: '',
          requirePaperTemplate: false
        };
      }
      
      if (c.hasMultipleItems === true || c.type === 'multiple') {
        const mappedItems = (c.items || []).map((i, itemIdx) => {
          let mappedEndDate = '';
          if (i.endDate) {
            if (typeof i.endDate === 'object' && i.endDate.$date) {
              mappedEndDate = new Date(i.endDate.$date).toISOString().slice(0, 10);
            } else if (typeof i.endDate === 'string') {
              mappedEndDate = new Date(i.endDate).toISOString().slice(0, 10);
            } else {
              mappedEndDate = i.endDate;
            }
          }
          return {
            name: i.name,
            referenceMaxMarks: i.referenceMaxMarks,
            endDate: mappedEndDate,
            isFrozen: i.isFrozen || false,
            requirePaperTemplate: typeof i.requirePaperTemplate === 'boolean' ? i.requirePaperTemplate : false
          };
        });
        
        return {
          id: Date.now() + idx,
          name: c.name,
          percentageOfCie: c.percentageOfCie,
          status: c.status || 'active',
          isFrozen: c.isFrozen || false,
          isVisibleToFaculty: c.isVisibleToFaculty !== undefined ? c.isVisibleToFaculty : true,
          allowFacultyEdit: c.allowFacultyEdit !== undefined ? c.allowFacultyEdit : true,
          isVisibleToStudents: c.isVisibleToStudents !== undefined ? c.isVisibleToStudents : true,
          type: 'multiple',
          items: mappedItems,
          aggregationType: c.aggregationRule?.type || 'average_of_all',
          nValue: c.aggregationRule?.nValue || 1,
          attendanceRules: [],
          referenceMaxMarks: null,
          hasEndDate: false,
          singleItemEndDate: '',
          requirePaperTemplate: typeof c.requirePaperTemplate === 'boolean' ? c.requirePaperTemplate : false
        };
      }
      
      let mappedSingleItemEndDate = '';
      if (c.singleItemEndDate) {
        if (typeof c.singleItemEndDate === 'object' && c.singleItemEndDate.$date) {
          mappedSingleItemEndDate = new Date(c.singleItemEndDate.$date).toISOString().slice(0, 10);
        } else if (typeof c.singleItemEndDate === 'string') {
          mappedSingleItemEndDate = new Date(c.singleItemEndDate).toISOString().slice(0, 10);
        } else {
          mappedSingleItemEndDate = c.singleItemEndDate;
        }
      }
      return {
        id: Date.now() + idx,
        name: c.name,
        percentageOfCie: c.percentageOfCie,
        status: c.status || 'active',
        isFrozen: c.isFrozen || false,
        isVisibleToFaculty: c.isVisibleToFaculty !== undefined ? c.isVisibleToFaculty : true,
        allowFacultyEdit: c.allowFacultyEdit !== undefined ? c.allowFacultyEdit : true,
        isVisibleToStudents: c.isVisibleToStudents !== undefined ? c.isVisibleToStudents : true,
        type: 'single',
        items: [],
        aggregationType: 'average_of_all',
        nValue: 1,
        attendanceRules: [],
        referenceMaxMarks: c.referenceMaxMarks,
        hasEndDate: !!mappedSingleItemEndDate,
        singleItemEndDate: mappedSingleItemEndDate,
        requirePaperTemplate: typeof c.requirePaperTemplate === 'boolean' ? c.requirePaperTemplate : false
      };
    });
    
    // Map End Exam with multiple items support
    const endExamData = item.endExam || {};
    const mappedEndExamItems = (endExamData.items || []).map((i, idx) => {
      let mappedEndDate = '';
      if (i.endDate) {
        if (typeof i.endDate === 'object' && i.endDate.$date) {
          mappedEndDate = new Date(i.endDate.$date).toISOString().slice(0, 10);
        } else if (typeof i.endDate === 'string') {
          mappedEndDate = new Date(i.endDate).toISOString().slice(0, 10);
        } else {
          mappedEndDate = i.endDate;
        }
      }
      return {
        itemId: i.itemId || Date.now() + idx,
        name: i.name || '',
        referenceMaxMarks: i.referenceMaxMarks || 0,
        endDate: mappedEndDate,
        isFrozen: i.isFrozen || false,
        requirePaperTemplate: typeof i.requirePaperTemplate === 'boolean' ? i.requirePaperTemplate : false
      };
    });
    
    setFormData({
      name: item.name,
      description: item.description || '',
      templateType: item.templateType || 'both',
      finalGradeCombination: item.finalGradeCombination || { ciePercentage: 40, endExamPercentage: 60 },
      cieEnabled: item.cie?.enabled !== false,
      cieComponents: mappedComponents,
      endExamEnabled: item.endExam?.enabled !== false,
      endExam: {
        name: endExamData.name || 'End Examination',
        status: endExamData.status || 'active',
        isVisibleToFaculty: endExamData.isVisibleToFaculty !== undefined ? endExamData.isVisibleToFaculty : true,
        allowFacultyEdit: endExamData.allowFacultyEdit !== undefined ? endExamData.allowFacultyEdit : true,
        isVisibleToStudents: endExamData.isVisibleToStudents !== undefined ? endExamData.isVisibleToStudents : false,
        requirePaperTemplate: endExamData.requirePaperTemplate === true,
        hasEndDate: endExamData.hasEndDate || false,
        endDate: endExamData.endDate || '',
        hasMultipleItems: endExamData.hasMultipleItems || false,
        items: mappedEndExamItems,
        aggregationType: endExamData.aggregationRule?.type || 'sum_of_all',
        nValue: endExamData.aggregationRule?.nValue || 1,
        totalMaxMarks: endExamData.totalMaxMarks || (mappedEndExamItems.length > 0 ? mappedEndExamItems.reduce((sum, i) => sum + (i.referenceMaxMarks || 0), 0) : null)
      }
    });
    setShowModal(true);
    setCurrentStep(1);
  };

  const confirmDeleteDefinition = (id, name) => {
    setDeleteDefinitionId(id);
    setDeleteDefinitionName(name);
    setShowDeleteDefinitionModal(true);
  };

  const handleDeleteDefinition = async () => {
    if (!deleteDefinitionId) return;
    setDeleteDefinitionLoading(true);
    try {
      await api.delete(`/api/cie/cie-component/${deleteDefinitionId}`, { 
        data: { archivedBy: localStorage.getItem('userId'), archiveReason: 'Deleted by user' } 
      });
      fetchDefinitionsCallback();
      showAlertMessage('Template deleted successfully', 'success');
      setShowDeleteDefinitionModal(false);
    } catch (error) {
      showAlertMessage('Error deleting template', 'error');
    } finally {
      setDeleteDefinitionLoading(false);
      setDeleteDefinitionId(null);
      setDeleteDefinitionName('');
    }
  };

  const toggleComponentStatus = async (definitionId, componentId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/api/cie/cie-component/${definitionId}/component-status`, {
        componentId,
        status: newStatus,
        updatedBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage(`Component ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch (error) {
      showAlertMessage('Error updating component status', 'error');
    }
  };

  const freezeComponent = async (definitionId, componentId) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/freeze-component`, {
        componentId,
        frozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage('Component frozen successfully', 'success');
    } catch (error) {
      showAlertMessage('Error freezing component', 'error');
    }
  };

  const unfreezeComponent = async (definitionId, componentId) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/unfreeze-component`, {
        componentId,
        unfrozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage('Component unfrozen successfully', 'success');
    } catch (error) {
      showAlertMessage('Error unfreezing component', 'error');
    }
  };

  const freezeAllComponents = async (definitionId) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/freeze-all`, {
        frozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage('All components frozen successfully', 'success');
    } catch (error) {
      showAlertMessage('Error freezing components', 'error');
    }
  };

  const unfreezeAllComponents = async (definitionId) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/unfreeze-all`, {
        unfrozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage('All components unfrozen successfully', 'success');
    } catch (error) {
      showAlertMessage('Error unfreezing components', 'error');
    }
  };

  const updateDefinitionStatus = async (definitionId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/api/cie/cie-component/${definitionId}/definition-status`, {
        status: newStatus,
        updatedBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage(`Template ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch (error) {
      showAlertMessage('Error updating template status', 'error');
    }
  };

  const freezeChildItem = async (definitionId, componentId, itemId, itemName) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/freeze-child-item`, {
        componentId,
        itemId,
        frozenBy: localStorage.getItem('userId'),
        freezeReason: `Item "${itemName}" frozen by COE`
      });
      fetchDefinitionsCallback();
      showAlertMessage(`Item "${itemName}" frozen successfully`, 'success');
    } catch (error) {
      showAlertMessage('Error freezing item', 'error');
    }
  };

  const unfreezeChildItem = async (definitionId, componentId, itemId, itemName) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/unfreeze-child-item`, {
        componentId,
        itemId,
        unfrozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage(`Item "${itemName}" unfrozen successfully`, 'success');
    } catch (error) {
      showAlertMessage('Error unfreezing item', 'error');
    }
  };

  // NEW: Freeze/Unfreeze End Exam item
  const freezeEndExamItem = async (definitionId, itemId, itemName) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/freeze-end-exam-item`, {
        itemId,
        frozenBy: localStorage.getItem('userId'),
        freezeReason: `End Exam item "${itemName}" frozen by COE`
      });
      fetchDefinitionsCallback();
      showAlertMessage(`End Exam item "${itemName}" frozen successfully`, 'success');
    } catch (error) {
      showAlertMessage('Error freezing end exam item', 'error');
    }
  };

  const unfreezeEndExamItem = async (definitionId, itemId, itemName) => {
    try {
      await api.post(`/api/cie/cie-component/${definitionId}/unfreeze-end-exam-item`, {
        itemId,
        unfrozenBy: localStorage.getItem('userId')
      });
      fetchDefinitionsCallback();
      showAlertMessage(`End Exam item "${itemName}" unfrozen successfully`, 'success');
    } catch (error) {
      showAlertMessage('Error unfreezing end exam item', 'error');
    }
  };

  const isAnyComponentFrozen = (def) => {
    if (def.endExam?.isFrozen) return true;
    if (def.endExam?.items?.some(item => item.isFrozen)) return true;
    return (def.cie?.components || []).some(c => c.isFrozen);
  };

  const subjectTypeOptions = [
    { value: 'theory', label: 'Theory', description: 'Regular theory subjects' },
    { value: 'lab', label: 'Lab', description: 'Laboratory/practical subjects' },
    { value: 'elective', label: 'Elective', description: 'Elective/optional subjects' },
    { value: 'language', label: 'Language', description: 'Language subjects' },
    { value: 'project', label: 'Project', description: 'Project/dissertation subjects' },
    { value: 'custom', label: 'Custom', description: 'Custom subject types' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <COEHeader orgInfo={orgInfo} tenant={tenant} onLogout={() => navigate('/coe')} />
      
      <main className="px-4 py-6 max-w-7xl mx-auto">
        {showAlert.show && (
          <div className="fixed top-20 right-4 z-[9999] animate-slide-in">
            <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${showAlert.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {showAlert.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span className="text-sm">{showAlert.message}</span>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Assessment Template Manager</h1>
          <p className="text-gray-500 text-sm">Create templates for different assessment types (Both, CIE Only, End Exam Only)</p>
        </div>

        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">1</div>
            <h2 className="font-semibold text-gray-700">Select Academic Context</h2>
          </div>
          <AcademicContextSelector
            tenantId={localStorage.getItem('tenantId')}
            onContextChange={handleContextChange}
            setProgramTypes={setProgramTypes}
            setPrograms={setPrograms}
            setAcademicYears={setAcademicYears}
            hideSemestersAndTerms={true}
          />
          {contextSelected && (
            <div className="mt-3 text-sm text-green-600 bg-green-50 p-2 rounded flex items-center gap-2">
              <CheckCircle size={14} /> {selectedProgramType?.name} → {selectedProgram?.name} → {selectedAcademicYear?.name}
            </div>
          )}
        </div>

        {contextSelected ? (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">2</div>
                <h2 className="font-semibold text-gray-700">Assessment Templates</h2>
              </div>
              <button onClick={() => { resetForm(); setShowModal(true); setCurrentStep(1); }} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                <Plus size={16} /> Create Template
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8"><RefreshCw className="animate-spin mx-auto text-gray-400" /></div>
            ) : definitions.length === 0 ? (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                <Layers className="mx-auto text-gray-300 mb-2" size={40} />
                No templates yet. Click "Create Template" to start.
              </div>
            ) : (
              <div className="space-y-4">
                {definitions.map((def) => {
                  const cieTotal = (def.cie.components || []).reduce((sum, comp) => sum + (comp.percentageOfCie || 0), 0);
                  const rules = def.matchingRules || {};
                  const hasMatchingRules = rules.applicableSubjectTypes?.length > 0 || 
                                           rules.subjectCodePatterns?.length > 0 || 
                                           rules.subjectNamePatterns?.length > 0 ||
                                           rules.isLab !== null ||
                                           rules.isElective !== null;
                  
                  const endExamHasMultipleItems = def.endExam?.hasMultipleItems === true;
                  const endExamItems = def.endExam?.items || [];
                  
                  return (
                    <div key={def._id} className="bg-white rounded-lg border overflow-hidden shadow-sm hover:shadow-md transition">
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b flex justify-between items-center flex-wrap gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{def.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                              def.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${def.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                              {def.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              def.templateType === 'both' ? 'bg-blue-100 text-blue-700' :
                              def.templateType === 'cie_only' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {def.templateType === 'both' ? 'Both' : def.templateType === 'cie_only' ? 'CIE Only' : 'End Exam Only'}
                            </span>
                            {hasMatchingRules && (
                              <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full flex items-center gap-1">
                                <Filter size={10} /> Auto-matching Enabled
                              </span>
                            )}
                            {endExamHasMultipleItems && endExamItems.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full flex items-center gap-1">
                                <Layers size={10} /> End Exam: {endExamItems.length} items
                              </span>
                            )}
                          </div>
                          {def.description && <p className="text-gray-500 text-sm mt-1">{def.description}</p>}
                          <div className="text-xs text-gray-400 mt-1 flex gap-3 flex-wrap">
                            {def.templateType === 'both' && (
                              <span>📊 Split: CIE {def.finalGradeCombination?.ciePercentage}% / End Exam {def.finalGradeCombination?.endExamPercentage}%</span>
                            )}
                            {def.templateType !== 'end_exam_only' && (
                              <span>📚 CIE: {def.cie.components?.length || 0} components ({cieTotal}%)</span>
                            )}
                            {def.templateType !== 'cie_only' && def.endExam.enabled && (
                              <span>📝 End Exam: {endExamHasMultipleItems ? `${endExamItems.length} items (${endExamItems.reduce((s, i) => s + (i.referenceMaxMarks || 0), 0)} marks)` : 'Single component'}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isAnyComponentFrozen(def) ? (
                            <button onClick={() => unfreezeAllComponents(def._id)} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1 hover:bg-green-200">
                              <Unlock size={12} /> Unfreeze All
                            </button>
                          ) : (
                            <button onClick={() => freezeAllComponents(def._id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1 hover:bg-red-200">
                              <Lock size={12} /> Freeze All
                            </button>
                          )}
                          <button onClick={() => updateDefinitionStatus(def._id, def.status)} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${def.status === 'active' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                            <Power size={12} /> {def.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => editDefinition(def)} className="p-1.5 text-gray-500 hover:text-amber-600 rounded hover:bg-amber-50" title="Edit">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => confirmDeleteDefinition(def._id, def.name)} className="p-1.5 text-gray-500 hover:text-red-600 rounded hover:bg-red-50" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="p-4">
                        {def.templateType !== 'end_exam_only' && (
                          <>
                            <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                              <PieChart size={14} /> CIE Components
                            </h4>
                            <div className="space-y-3">
                              {(def.cie.components || []).map((comp, idx) => {
                                const isExpanded = mainExpanded[def._id]?.[idx] || false;
                                return (
                                  <div key={comp.componentId || idx} className="border rounded-lg overflow-hidden">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer" onClick={() => {
                                      setMainExpanded(prev => ({
                                        ...prev,
                                        [def._id]: {
                                          ...prev[def._id],
                                          [idx]: !isExpanded
                                        }
                                      }));
                                    }}>
                                      <div className="flex items-center gap-3 flex-wrap">
                                        <span className={`w-2 h-2 rounded-full ${comp.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                        <span className="font-medium">{comp.name}</span>
                                        <span className="text-sm text-gray-500">{comp.percentageOfCie}% of CIE</span>
                                        {comp.isFrozen && (
                                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded flex items-center gap-1">
                                            <Lock size={10} /> Frozen
                                          </span>
                                        )}
                                        {comp.status === 'inactive' && (
                                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Disabled</span>
                                        )}
                                      </div>
                                      <div className="flex gap-1 items-center">
                                        <button onClick={e => { e.stopPropagation(); toggleComponentStatus(def._id, comp.componentId, comp.status); }} className={`p-1 rounded text-xs ${comp.status === 'active' ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`} title={comp.status === 'active' ? 'Disable' : 'Enable'}>
                                          <Power size={14} />
                                        </button>
                                        {comp.isFrozen ? (
                                          <button onClick={e => { e.stopPropagation(); unfreezeComponent(def._id, comp.componentId); }} className="p-1 text-green-500 hover:text-green-700 rounded text-xs" title="Unfreeze">
                                            <Unlock size={14} />
                                          </button>
                                        ) : (
                                          <button onClick={e => { e.stopPropagation(); freezeComponent(def._id, comp.componentId); }} className="p-1 text-red-500 hover:text-red-700 rounded text-xs" title="Freeze">
                                            <Lock size={14} />
                                          </button>
                                        )}
                                        <ChevronRight size={18} className={`ml-2 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <>
                                        <div className="px-3 py-1.5 bg-white text-xs flex gap-4 text-gray-500 border-t">
                                          <span className="flex items-center gap-1">
                                            {comp.isVisibleToFaculty ? <EyeIcon size={12} className="text-green-600" /> : <EyeIcon size={12} className="text-gray-300" />}
                                            Faculty View
                                          </span>
                                          <span className="flex items-center gap-1">
                                            {comp.allowFacultyEdit ? <PenTool size={12} className="text-green-600" /> : <PenTool size={12} className="text-gray-300" />}
                                            Faculty Edit
                                          </span>
                                          <span className="flex items-center gap-1">
                                            {comp.isVisibleToStudents ? <EyeOff size={12} className="text-green-600" /> : <EyeOff size={12} className="text-gray-300" />}
                                            Student View
                                          </span>
                                          {comp.type === 'multiple' && comp.aggregationRule && (
                                            <span className="flex items-center gap-1">
                                              <TrendingUp size={12} />
                                              Aggregation: {comp.aggregationRule.type} {comp.aggregationRule.nValue ? `(N=${comp.aggregationRule.nValue})` : ''}
                                            </span>
                                          )}
                                        </div>
                                        {comp.type === 'multiple' && comp.items && comp.items.length > 0 && (
                                          <div className="px-3 py-2 bg-gray-50 border-t">
                                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                              <Calendar size={10} /> Items:
                                            </p>
                                            <div className="space-y-1.5">
                                              {comp.items.map((item, itemIdx) => (
                                                <div key={itemIdx} className="flex justify-between items-center text-sm p-1.5 bg-white rounded border">
                                                  <div className="flex items-center gap-2">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${item.isFrozen ? 'bg-red-400' : 'bg-green-400'}`}></span>
                                                    <span className="font-medium">{item.name}</span>
                                                    <span className="text-gray-500 text-xs">Max: {item.referenceMaxMarks}</span>
                                                  </div>
                                                  <div className="flex items-center gap-3">
                                                    {item.endDate && (
                                                      <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <Clock size={10} /> Deadline: {new Date(item.endDate).toLocaleDateString()}
                                                      </span>
                                                    )}
                                                    {item.isFrozen ? (
                                                      <button 
                                                        onClick={() => unfreezeChildItem(def._id, comp.componentId, item.itemId, item.name)} 
                                                        className="p-1 text-green-500 hover:text-green-700 rounded text-xs" 
                                                        title="Unfreeze"
                                                      >
                                                        <Unlock size={14} />
                                                      </button>
                                                    ) : (
                                                      <button 
                                                        onClick={() => freezeChildItem(def._id, comp.componentId, item.itemId, item.name)} 
                                                        className="p-1 text-red-500 hover:text-red-700 rounded text-xs" 
                                                        title="Freeze"
                                                      >
                                                        <Lock size={14} />
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {comp.type === 'single' && comp.referenceMaxMarks && (
                                          <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
                                            Reference Max Marks: {comp.referenceMaxMarks}
                                            {comp.singleItemEndDate && (
                                              <span className="ml-3">Deadline: {new Date(comp.singleItemEndDate).toLocaleDateString()}</span>
                                            )}
                                          </div>
                                        )}
                                        {comp.isAttendance && comp.attendanceRules && comp.attendanceRules.length > 0 && (
                                          <div className="px-3 py-2 bg-gray-50 border-t">
                                            <p className="text-xs text-gray-500 mb-1">Attendance Rules:</p>
                                            <div className="flex flex-wrap gap-2">
                                              {comp.attendanceRules.map((rule, rIdx) => (
                                                <span key={rIdx} className="text-xs bg-white px-2 py-0.5 rounded border shadow-sm">
                                                  {rule.minPercentage}-{rule.maxPercentage}% → {rule.marksPercentage}% of component
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                              {(!def.cie.components || def.cie.components.length === 0) && (
                                <div className="text-center text-gray-400 py-4 border-2 border-dashed rounded-lg">
                                  No CIE components defined
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {def.templateType !== 'cie_only' && def.endExam.enabled && (
                          <div className="mt-4 pt-3 border-t">
                            <h4 className="font-medium text-sm text-gray-700 mb-2 flex items-center gap-2">
                              <Award size={14} /> End Examination
                            </h4>
                            <div className="border rounded-lg p-3 bg-gray-50">
                              <div className="flex justify-between items-center flex-wrap gap-2 mb-2">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className={`w-2 h-2 rounded-full ${def.endExam.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                  <span className="font-medium">{def.endExam.name}</span>
                                  {def.endExam.isFrozen && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded"><Lock size={10} className="inline" /> Frozen</span>}
                                  {def.endExam.hasMultipleItems && def.endExam.items && def.endExam.items.length > 0 && (
                                    <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                      {def.endExam.items.length} items · Total: {def.endExam.items.reduce((s, i) => s + (i.referenceMaxMarks || 0), 0)} marks
                                    </span>
                                  )}
                                  {def.endExam.aggregationRule && def.endExam.hasMultipleItems && (
                                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                      Aggregation: {def.endExam.aggregationRule.type} {def.endExam.aggregationRule.nValue ? `(N=${def.endExam.aggregationRule.nValue})` : ''}
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-3 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">{def.endExam.isVisibleToFaculty ? <EyeIcon size={12} className="text-green-600" /> : <EyeIcon size={12} className="text-gray-300" />} Faculty</span>
                                  <span className="flex items-center gap-1">{def.endExam.allowFacultyEdit ? <PenTool size={12} className="text-green-600" /> : <PenTool size={12} className="text-gray-300" />} Edit</span>
                                  <span className="flex items-center gap-1">{def.endExam.isVisibleToStudents ? <EyeOff size={12} className="text-green-600" /> : <EyeOff size={12} className="text-gray-300" />} Students</span>
                                </div>
                              </div>
                              
                              {/* Show End Exam items if multiple items exist */}
                              {def.endExam.hasMultipleItems && def.endExam.items && def.endExam.items.length > 0 && (
                                <div className="mt-2 pt-2 border-t">
                                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                    <Layers size={10} /> End Exam Items:
                                  </p>
                                  <div className="space-y-1.5">
                                    {def.endExam.items.map((item, idx) => (
                                      <div key={idx} className="flex justify-between items-center text-sm p-1.5 bg-white rounded border">
                                        <div className="flex items-center gap-2">
                                          <span className={`w-1.5 h-1.5 rounded-full ${item.isFrozen ? 'bg-red-400' : 'bg-green-400'}`}></span>
                                          <span className="font-medium">{item.name}</span>
                                          <span className="text-gray-500 text-xs">Max: {item.referenceMaxMarks}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          {item.endDate && (
                                            <span className="text-xs text-gray-400 flex items-center gap-1">
                                              <Clock size={10} /> Deadline: {new Date(item.endDate).toLocaleDateString()}
                                            </span>
                                          )}
                                          {item.isFrozen ? (
                                            <button 
                                              onClick={() => unfreezeEndExamItem(def._id, item.itemId, item.name)} 
                                              className="p-1 text-green-500 hover:text-green-700 rounded text-xs" 
                                              title="Unfreeze"
                                            >
                                              <Unlock size={14} />
                                            </button>
                                          ) : (
                                            <button 
                                              onClick={() => freezeEndExamItem(def._id, item.itemId, item.name)} 
                                              className="p-1 text-red-500 hover:text-red-700 rounded text-xs" 
                                              title="Freeze"
                                            >
                                              <Lock size={14} />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {def.endExam.hasEndDate && def.endExam.endDate && !def.endExam.hasMultipleItems && (
                                <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                  <Calendar size={10} /> Deadline: {new Date(def.endExam.endDate).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center text-yellow-700 text-sm">
            Please select Program Type, Program, and Academic Year first
          </div>
        )}
      </main>

      {/* Delete Modals - Same as before */}
      {showDeleteDefinitionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Template</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deleteDefinitionName}</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteDefinitionModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleDeleteDefinition} disabled={deleteDefinitionLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {deleteDefinitionLoading ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteComponentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Component</h3>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete <span className="font-semibold">{deleteComponentName}</span>? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteComponentModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleDeleteComponent} disabled={deleteComponentLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {deleteComponentLoading ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Item</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deleteItemData.itemName}</span> from{' '}
              <span className="font-semibold">{deleteItemData.compName}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteItemModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleDeleteItem} disabled={deleteItemLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {deleteItemLoading ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Delete End Exam Item Modal */}
      {showDeleteEndExamItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete End Exam Item</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deleteEndExamItemData.itemName}</span> from End Exam? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteEndExamItemModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleDeleteEndExamItem} disabled={deleteEndExamItemLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                {deleteEndExamItemLoading ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal - Updated with End Exam multiple items support */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b p-4 sm:p-5 z-10">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h2 className="text-lg sm:text-xl font-bold">{editingItem ? 'Edit' : 'Create'} Assessment Template</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded self-end sm:self-auto"><X size={20} /></button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                {[
                  { step: 1, label: 'Template Type' },
                  { step: 2, label: 'CIE Components' },
                  { step: 3, label: 'End Exam' }
                ].map((item) => (
                  <div key={item.step} className="flex-1 w-full">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all cursor-pointer ${
                        currentStep >= item.step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`} onClick={() => setCurrentStep(item.step)}>
                        {item.step}
                      </div>
                      {item.step < 3 && (
                        <div className={`flex-1 h-0.5 mx-2 transition-all ${
                          currentStep > item.step ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${currentStep >= item.step ? 'text-blue-600' : 'text-gray-400'}`}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 sm:p-6">
              {/* STEP 1: Template Type Selection - Same as before */}
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-1">Template Name <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full border rounded-lg p-3 text-base" placeholder="e.g., UG Theory Standard" autoFocus />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full border rounded-lg p-3 text-base" rows="2" placeholder="Optional description" />
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">This template applies to:</p>
                    <p className="text-sm text-blue-700">{selectedProgramType?.name} → {selectedProgram?.name} → {selectedAcademicYear?.name}</p>
                  </div>

                  {/* Matching Rules Section */}
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowMatchingRules(!showMatchingRules)}
                      className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Filter size={18} className="text-indigo-600" />
                        <span className="font-medium text-gray-700">Subject Matching Rules (Optional)</span>
                        <span className="text-xs text-gray-400">Configure which subjects this template applies to</span>
                      </div>
                      {showMatchingRules ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    
                    {showMatchingRules && (
                      <div className="p-4 space-y-4 border-t">
                        <div>
                          <label className="block text-sm font-medium mb-2">Subject Types</label>
                          <div className="flex flex-wrap gap-2">
                            {subjectTypeOptions.map(option => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => toggleSubjectType(option.value)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                  matchingRules.applicableSubjectTypes.includes(option.value)
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border rounded-lg p-4 bg-white">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <PieChart size={18} /> Template Type
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">Select what type of assessment this template will be used for</p>
                    
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="radio" name="templateType" value="both" checked={selectedTemplateType === 'both'} onChange={() => handleTemplateTypeChange('both')} className="mt-1" />
                        <div>
                          <div className="font-medium">Both (CIE + End Exam)</div>
                          <div className="text-sm text-gray-500">For theory subjects that have both internal assessment and final examination</div>
                          <div className="text-xs text-gray-400 mt-1">Example: Mathematics, Physics, Chemistry</div>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="radio" name="templateType" value="cie_only" checked={selectedTemplateType === 'cie_only'} onChange={() => handleTemplateTypeChange('cie_only')} className="mt-1" />
                        <div>
                          <div className="font-medium">CIE Only</div>
                          <div className="text-sm text-gray-500">For lab, project, practical subjects that have only internal assessment</div>
                          <div className="text-xs text-gray-400 mt-1">Example: C Programming Lab, Physics Practical, Final Year Project</div>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="radio" name="templateType" value="end_exam_only" checked={selectedTemplateType === 'end_exam_only'} onChange={() => handleTemplateTypeChange('end_exam_only')} className="mt-1" />
                        <div>
                          <div className="font-medium">End Exam Only</div>
                          <div className="text-sm text-gray-500">For certification, training, external subjects that have only final examination</div>
                          <div className="text-xs text-gray-400 mt-1">Example: Industry Training, Certification Course, Online Course</div>
                        </div>
                      </label>
                    </div>

                    {selectedTemplateType === 'both' && (
                      <div className="mt-4 pt-4 border-t">
                        <label className="block text-sm font-medium mb-2">CIE Percentage of Final Grade</label>
                        <input 
                          type="number" 
                          value={formData.finalGradeCombination.ciePercentage} 
                          onChange={e => {
                            const cie = parseInt(e.target.value) || 0;
                            setFormData({
                              ...formData,
                              finalGradeCombination: {
                                ciePercentage: cie,
                                endExamPercentage: 100 - cie
                              }
                            });
                          }}
                          className="w-40 border rounded-lg p-3 text-lg font-semibold text-center"
                        />
                        <p className="text-xs text-gray-400 mt-1">End Exam will be {100 - formData.finalGradeCombination.ciePercentage}%</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2: CIE Components - Same as before (not shown fully for brevity) */}
              {currentStep === 2 && selectedTemplateType !== 'end_exam_only' && (
                <div className="space-y-5">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">CIE Components (must sum to 100%)</p>
                    <p className="text-xs text-blue-600 mt-1">Define the internal assessment components for this template.</p>
                  </div>

                  <div className="bg-white border rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Components (% of CIE)</div>
                        <p className="text-xs text-gray-500">Sum must equal 100%</p>
                      </div>
                      <button onClick={addComponent} className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg text-base font-medium hover:bg-blue-700 shadow-sm">
                        <PlusCircle size={20} /> Add Component
                      </button>
                    </div>
                    <div className={`mt-3 p-3 rounded-lg text-center ${isTotalValid() ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-medium">Total: {getTotalComponentPercentage()}% / 100%</span>
                        <div className="w-full max-w-md h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${Math.min(getTotalComponentPercentage(), 100)}%` }}></div>
                        </div>
                        {!isTotalValid() && (<span className="text-sm">{getTotalComponentPercentage() > 100 ? `⚠️ Reduce by ${getTotalComponentPercentage() - 100}%` : `⚠️ Add ${100 - getTotalComponentPercentage()}% more`}</span>)}
                      </div>
                    </div>
                  </div>

                  {formData.cieComponents.length === 0 ? (
                    <div className="text-center text-gray-400 py-12 border-2 border-dashed rounded-lg">
                      <Layers className="mx-auto mb-2" size={48} strokeWidth={1.5} />
                      <p>No components added yet</p>
                      <button onClick={addComponent} className="mt-3 text-blue-600 underline">Click here to add your first component</button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.cieComponents.map((comp, idx) => (
                        <div key={comp.id} className="border rounded-lg overflow-hidden">
                          <div className="flex justify-between items-center p-4 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggleComponentExpand(comp.id)}>
                            <div className="flex items-center gap-3 flex-wrap flex-1">
                              <span className="text-sm font-medium text-gray-500">#{idx + 1}</span>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${comp.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                <button onClick={(e) => { e.stopPropagation(); const newStatus = comp.status === 'active' ? 'inactive' : 'active'; updateComponent(idx, 'status', newStatus); }} className="text-xs text-gray-500 hover:text-amber-600">
                                  <Power size={12} />
                                </button>
                              </div>
                              <input type="text" placeholder="Component Name" value={comp.name} onClick={(e) => e.stopPropagation()} onChange={e => updateComponent(idx, 'name', e.target.value)} className="border rounded-lg px-3 py-2 text-base font-medium w-48" />
                              <input type="number" placeholder="% of CIE" value={comp.percentageOfCie} onClick={(e) => e.stopPropagation()} onChange={e => updateComponent(idx, 'percentageOfCie', parseFloat(e.target.value) || 0)} className="w-24 border rounded-lg px-3 py-2 text-center" />
                              <span className="text-sm text-gray-400">%</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={(e) => { e.stopPropagation(); duplicateComponent(idx); }} className="p-2 text-gray-400 hover:text-blue-600 rounded" title="Duplicate"><Copy size={16} /></button>
                              <button onClick={(e) => { e.stopPropagation(); confirmDeleteComponent(idx, comp.name); }} className="p-2 text-gray-400 hover:text-red-600 rounded" title="Remove"><Trash size={16} /></button>
                              <ChevronRight size={18} className={`text-gray-400 transition-transform ${expandedComponents[comp.id] ? 'rotate-90' : ''}`} />
                            </div>
                          </div>

                          {expandedComponents[comp.id] && (
                            <div className="p-4 border-t bg-white space-y-4">
                              <div className="grid grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={comp.isVisibleToFaculty} onChange={e => updateComponent(idx, 'isVisibleToFaculty', e.target.checked)} /><EyeIcon size={14} /> Visible to Faculty</label>
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={comp.allowFacultyEdit} onChange={e => updateComponent(idx, 'allowFacultyEdit', e.target.checked)} /><PenTool size={14} /> Allow Faculty Edit</label>
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={comp.isVisibleToStudents} onChange={e => updateComponent(idx, 'isVisibleToStudents', e.target.checked)} /><EyeOff size={14} /> Visible to Students</label>
                                {((comp.type === 'single') || (comp.type === 'multiple' && (!comp.items || comp.items.length === 0))) && (
                                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={comp.requirePaperTemplate} onChange={e => updateComponent(idx, 'requirePaperTemplate', e.target.checked)} /><Layers size={14} /> Require Paper Template</label>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-4 pb-3 border-b">
                                <label className="flex items-center gap-2"><input type="radio" name={`comp_type_${comp.id}`} checked={comp.type === 'single'} onChange={() => updateComponent(idx, 'type', 'single')} /><span className="text-sm">Single Item</span></label>
                                <label className="flex items-center gap-2"><input type="radio" name={`comp_type_${comp.id}`} checked={comp.type === 'multiple'} onChange={() => updateComponent(idx, 'type', 'multiple')} /><span className="text-sm">Multiple Items</span></label>
                                <label className="flex items-center gap-2"><input type="radio" name={`comp_type_${comp.id}`} checked={comp.type === 'attendance'} onChange={() => updateComponent(idx, 'type', 'attendance')} /><span className="text-sm">Attendance</span></label>
                              </div>

                              {comp.type === 'single' && (
                                <div>
                                  <div className="mb-3">
                                    <label className="block text-sm font-medium mb-1">Reference Max Marks</label>
                                    <input type="number" value={comp.referenceMaxMarks || ''} onChange={e => updateComponent(idx, 'referenceMaxMarks', parseFloat(e.target.value) || 0)} className="w-32 border rounded-lg p-2" placeholder="e.g., 100" />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={comp.hasEndDate} onChange={e => updateComponent(idx, 'hasEndDate', e.target.checked)} /><span>Set Deadline</span></label>
                                    {comp.hasEndDate && (<input type="date" value={comp.singleItemEndDate || ''} onChange={e => updateComponent(idx, 'singleItemEndDate', e.target.value)} className="border rounded p-2" />)}
                                  </div>
                                </div>
                              )}

                              {comp.type === 'multiple' && (
                                <div>
                                  <div className="flex justify-between items-center mb-3">
                                    <label className="text-sm font-medium">Items</label>
                                    <button onClick={() => addItem(idx)} className="text-sm text-blue-600 flex items-center gap-1"><PlusCircle size={14} /> Add Item</button>
                                  </div>
                                  {comp.items.map((item, itemIdx) => (
                                    <div key={item.itemId} className="border rounded-lg mb-3">
                                      <div className="flex justify-between items-center p-2 bg-gray-50 cursor-pointer" onClick={() => toggleItemExpand(comp.id, itemIdx)}>
                                        <div className="flex gap-2 flex-1 items-center">
                                          <input type="text" placeholder="Item name" value={item.name} onClick={(e) => e.stopPropagation()} onChange={e => updateItem(idx, itemIdx, 'name', e.target.value)} className="flex-1 border rounded p-2 text-sm" />
                                          <input type="number" placeholder="Reference Max Marks" value={item.referenceMaxMarks} onClick={(e) => e.stopPropagation()} onChange={e => updateItem(idx, itemIdx, 'referenceMaxMarks', parseFloat(e.target.value) || 0)} className="w-32 border rounded p-2 text-sm text-center" />
                                          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={item.requirePaperTemplate} onChange={e => updateItem(idx, itemIdx, 'requirePaperTemplate', e.target.checked)} /><Layers size={12} /> Require Paper Template</label>
                                          <button onClick={(e) => { e.stopPropagation(); confirmDeleteItem(idx, itemIdx, item.name, comp.name); }} className="text-red-500 px-2">✕</button>
                                        </div>
                                        <ChevronRight size={14} className={`text-gray-400 transition-transform ${expandedItems[`${comp.id}_${itemIdx}`] ? 'rotate-90' : ''}`} />
                                      </div>
                                      {expandedItems[`${comp.id}_${itemIdx}`] && (
                                        <div className="p-3 border-t bg-white">
                                          <div className="flex items-center gap-2">
                                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.endDate} onChange={e => updateItem(idx, itemIdx, 'endDate', e.target.checked ? new Date().toISOString().slice(0, 10) : '')} /><span>Set Deadline</span></label>
                                            {item.endDate && (<input type="date" value={item.endDate} onChange={e => updateItem(idx, itemIdx, 'endDate', e.target.value)} className="border rounded p-1 text-sm" />)}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}

                                  <div className="mt-4 pt-3 border-t">
                                    <label className="text-sm font-medium mb-2 block">How to combine these items?</label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      <label className="flex items-center gap-2"><input type="radio" name={`agg_${comp.id}`} checked={comp.aggregationType === 'best_of_n'} onChange={() => updateComponent(idx, 'aggregationType', 'best_of_n')} /><span className="text-sm">Best of N</span></label>
                                      <label className="flex items-center gap-2"><input type="radio" name={`agg_${comp.id}`} checked={comp.aggregationType === 'average_of_all'} onChange={() => updateComponent(idx, 'aggregationType', 'average_of_all')} /><span className="text-sm">Average of all</span></label>
                                      <label className="flex items-center gap-2"><input type="radio" name={`agg_${comp.id}`} checked={comp.aggregationType === 'sum_of_all'} onChange={() => updateComponent(idx, 'aggregationType', 'sum_of_all')} /><span className="text-sm">Sum of all</span></label>
                                      <label className="flex items-center gap-2"><input type="radio" name={`agg_${comp.id}`} checked={comp.aggregationType === 'last_n'} onChange={() => updateComponent(idx, 'aggregationType', 'last_n')} /><span className="text-sm">Last N tests</span></label>
                                      <label className="flex items-center gap-2"><input type="radio" name={`agg_${comp.id}`} checked={comp.aggregationType === 'first_n'} onChange={() => updateComponent(idx, 'aggregationType', 'first_n')} /><span className="text-sm">First N tests</span></label>
                                    </div>
                                    {(comp.aggregationType === 'best_of_n' || comp.aggregationType === 'last_n' || comp.aggregationType === 'first_n') && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <span className="text-sm">Value of N:</span>
                                        <input type="number" min="1" max={comp.items.length} value={comp.nValue} onChange={e => updateComponent(idx, 'nValue', parseInt(e.target.value) || 1)} className="w-20 border rounded p-1.5 text-center" />
                                        <span className="text-xs text-gray-500">(Out of {comp.items.length} items)</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {comp.type === 'attendance' && (
                                <div>
                                  <div className="flex justify-between items-center mb-3">
                                    <label className="text-sm font-medium">Attendance Rules</label>
                                    <button onClick={() => addAttendanceRule(idx)} className="text-sm text-blue-600 flex items-center gap-1"><PlusCircle size={14} /> Add Rule</button>
                                  </div>
                                  {comp.attendanceRules.map((rule, ruleIdx) => (
                                    <div key={ruleIdx} className="flex gap-2 mb-2 items-center flex-wrap">
                                      <span className="text-sm">If attendance</span>
                                      <input type="number" value={rule.minPercentage} onChange={e => updateAttendanceRule(idx, ruleIdx, 'minPercentage', parseFloat(e.target.value) || 0)} className="w-20 border rounded p-1.5 text-sm text-center" />
                                      <span>-</span>
                                      <input type="number" value={rule.maxPercentage} onChange={e => updateAttendanceRule(idx, ruleIdx, 'maxPercentage', parseFloat(e.target.value) || 0)} className="w-20 border rounded p-1.5 text-sm text-center" />
                                      <span>% →</span>
                                      <input type="number" value={rule.marksPercentage} onChange={e => updateAttendanceRule(idx, ruleIdx, 'marksPercentage', parseFloat(e.target.value) || 0)} className="w-20 border rounded p-1.5 text-sm text-center" />
                                      <span>% of component</span>
                                      <button onClick={() => removeAttendanceRule(idx, ruleIdx)} className="text-red-500">✕</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3: End Exam Configuration - UPDATED with multiple items support */}
              {currentStep === 3 && selectedTemplateType !== 'cie_only' && (
                <div className="space-y-5">
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-purple-800">
                      End Examination Configuration
                    </p>
                    {selectedTemplateType === 'both' && (
                      <p className="text-xs text-purple-600 mt-1">
                        End Exam contributes {formData.finalGradeCombination?.endExamPercentage || 60}% of final grade
                      </p>
                    )}
                    {selectedTemplateType === 'end_exam_only' && (
                      <p className="text-xs text-purple-600 mt-1">
                        End Exam contributes 100% of final grade
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Exam Name</label>
                      <input type="text" value={formData.endExam.name} onChange={e => setFormData({...formData, endExam: {...formData.endExam, name: e.target.value}})} className="w-full border rounded-lg p-3" placeholder="e.g., End Semester Examination" />
                    </div>
                  </div>
                  
                  {/* NEW: Multiple Items Toggle for End Exam */}
                  <div className="border rounded-lg p-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.endExam.hasMultipleItems}
                        onChange={e => setFormData({
                          ...formData,
                          endExam: {
                            ...formData.endExam,
                            hasMultipleItems: e.target.checked,
                            items: e.target.checked ? formData.endExam.items : [],
                            aggregationType: e.target.checked ? formData.endExam.aggregationType : 'sum_of_all'
                          }
                        })}
                      />
                      <Layers size={14} /> This End Exam has multiple sub-components (e.g., Theory + Practical + Viva)
                    </label>
                  </div>

                  {/* Multiple Items Section */}
                  {formData.endExam.hasMultipleItems && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <label className="text-sm font-medium">End Exam Items</label>
                        <button onClick={addEndExamItem} className="text-sm text-blue-600 flex items-center gap-1"><PlusCircle size={14} /> Add Item</button>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">Define each component of the end exam (e.g., Theory Paper, Practical Exam, Viva Voce)</div>
                      
                      {formData.endExam.items.length === 0 && (
                        <div className="text-center text-gray-400 py-6 border-2 border-dashed rounded-lg">
                          <Layers className="mx-auto mb-2" size={32} strokeWidth={1.5} />
                          <p className="text-sm">No items added yet</p>
                          <button onClick={addEndExamItem} className="mt-2 text-blue-600 underline text-sm">Click here to add your first end exam item</button>
                        </div>
                      )}
                      
                      {formData.endExam.items.map((item, itemIdx) => (
                        <div key={item.itemId} className="border rounded-lg mb-3 bg-white">
                          <div className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer" onClick={() => toggleEndExamItemExpand(itemIdx)}>
                            <div className="flex gap-3 flex-1 items-center flex-wrap">
                              <input 
                                type="text" 
                                placeholder="Item name (e.g., Theory Paper)" 
                                value={item.name} 
                                onClick={(e) => e.stopPropagation()} 
                                onChange={e => updateEndExamItem(itemIdx, 'name', e.target.value)} 
                                className="flex-1 min-w-[150px] border rounded p-2 text-sm" 
                              />
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">Max Marks:</span>
                                <input 
                                  type="number" 
                                  placeholder="Marks" 
                                  value={item.referenceMaxMarks} 
                                  onClick={(e) => e.stopPropagation()} 
                                  onChange={e => updateEndExamItem(itemIdx, 'referenceMaxMarks', parseFloat(e.target.value) || 0)} 
                                  className="w-24 border rounded p-2 text-sm text-center" 
                                />
                              </div>
                              <label className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={item.requirePaperTemplate} onChange={e => updateEndExamItem(itemIdx, 'requirePaperTemplate', e.target.checked)} />
                                <Layers size={12} /> Require Paper Template
                              </label>
                              <button 
                                onClick={(e) => { e.stopPropagation(); confirmDeleteEndExamItem(itemIdx, item.name || `Item ${itemIdx + 1}`); }} 
                                className="text-red-500 hover:text-red-700 px-2"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                            <ChevronRight size={14} className={`text-gray-400 transition-transform ${expandedEndExamItems[itemIdx] ? 'rotate-90' : ''}`} />
                          </div>
                          {expandedEndExamItems[itemIdx] && (
                            <div className="p-3 border-t bg-white">
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                  <input type="checkbox" checked={!!item.endDate} onChange={e => updateEndExamItem(itemIdx, 'endDate', e.target.checked ? new Date().toISOString().slice(0, 10) : '')} />
                                  <Calendar size={12} /> Set Deadline for this item
                                </label>
                                {item.endDate && (
                                  <input 
                                    type="date" 
                                    value={item.endDate} 
                                    onChange={e => updateEndExamItem(itemIdx, 'endDate', e.target.value)} 
                                    className="border rounded p-1.5 text-sm" 
                                  />
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-2">After deadline, faculty cannot edit marks for this item</p>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {formData.endExam.items.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex justify-between items-center">
                            <div>
                              <label className="text-sm font-medium block mb-1">How to combine these items?</label>
                              <div className="flex flex-wrap gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                  <input type="radio" name="endExamAgg" checked={formData.endExam.aggregationType === 'sum_of_all'} onChange={() => setFormData(prev => ({...prev, endExam: {...prev.endExam, aggregationType: 'sum_of_all'}}))} />
                                  <span>Sum of all items</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                  <input type="radio" name="endExamAgg" checked={formData.endExam.aggregationType === 'average_of_all'} onChange={() => setFormData(prev => ({...prev, endExam: {...prev.endExam, aggregationType: 'average_of_all'}}))} />
                                  <span>Average of all items</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                  <input type="radio" name="endExamAgg" checked={formData.endExam.aggregationType === 'best_of_n'} onChange={() => setFormData(prev => ({...prev, endExam: {...prev.endExam, aggregationType: 'best_of_n'}}))} />
                                  <span>Best of N items</span>
                                </label>
                              </div>
                            </div>
                            {formData.endExam.aggregationType === 'best_of_n' && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm">N =</span>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max={formData.endExam.items.length} 
                                  value={formData.endExam.nValue} 
                                  onChange={e => setFormData(prev => ({...prev, endExam: {...prev.endExam, nValue: parseInt(e.target.value) || 1}}))} 
                                  className="w-16 border rounded p-1.5 text-center text-sm" 
                                />
                                <span className="text-xs text-gray-500">(Out of {formData.endExam.items.length} items)</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                            <strong>Total Max Marks:</strong> {formData.endExam.items.reduce((sum, i) => sum + (i.referenceMaxMarks || 0), 0)} marks
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Single item configuration (legacy) - only show if hasMultipleItems is false */}
                  {!formData.endExam.hasMultipleItems && (
                    <>
                      <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.endExam.isVisibleToFaculty} onChange={e => setFormData({...formData, endExam: {...formData.endExam, isVisibleToFaculty: e.target.checked}})} /><EyeIcon size={14} /> Visible to Faculty</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.endExam.allowFacultyEdit} onChange={e => setFormData({...formData, endExam: {...formData.endExam, allowFacultyEdit: e.target.checked}})} /><PenTool size={14} /> Allow Faculty Edit</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.endExam.isVisibleToStudents} onChange={e => setFormData({...formData, endExam: {...formData.endExam, isVisibleToStudents: e.target.checked}})} /><EyeOff size={14} /> Visible to Students</label>
                      </div>

                      <div className="border rounded-lg p-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!formData.endExam.requirePaperTemplate} onChange={e => setFormData({...formData, endExam: {...formData.endExam, requirePaperTemplate: e.target.checked}})} />
                          <Layers size={14} /> Require Paper Template for End Exam
                        </label>
                      </div>

                      <div className="border rounded-lg p-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={formData.endExam.hasEndDate} onChange={e => setFormData({...formData, endExam: {...formData.endExam, hasEndDate: e.target.checked}})} />
                          <Calendar size={14} /> Set deadline for faculty to enter marks
                        </label>
                        {formData.endExam.hasEndDate && (
                          <input type="date" value={formData.endExam.endDate} onChange={e => setFormData({...formData, endExam: {...formData.endExam, endDate: e.target.value}})} className="mt-2 border rounded-lg p-2 w-full" />
                        )}
                        <p className="text-xs text-gray-400 mt-1">After deadline, faculty cannot edit marks</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {currentStep === 3 && selectedTemplateType === 'cie_only' && (
                <div className="bg-yellow-50 p-6 rounded-lg text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award size={32} className="text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">No End Exam Needed</h3>
                  <p className="text-yellow-700">This is a <strong>CIE Only</strong> template. No end examination is required.</p>
                  <p className="text-yellow-600 text-sm mt-2">Click "Create Template" to save your template.</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-between">
              <button onClick={prevStep} className={`px-5 py-2.5 border rounded-lg text-base font-medium ${currentStep === 1 ? 'invisible' : 'hover:bg-gray-100'}`}>← Previous</button>
              <div className="flex gap-3">
                <button onClick={() => setShowModal(false)} className="px-5 py-2.5 border rounded-lg text-base">Cancel</button>
                {currentStep < 3 ? (
                  <button onClick={nextStep} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-base font-medium hover:bg-blue-700">Next →</button>
                ) : (
                  <button onClick={handleSave} className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-base font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-60" disabled={saveLoading}>
                    {saveLoading ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                    {editingItem ? (saveLoading ? 'Updating...' : 'Update Template') : (saveLoading ? 'Creating...' : 'Create Template')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamComponentManager;